import { useCallback, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { CREATE_WITH_ROVO_ANNOUNCEMENT_SPLASH } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { useSiteUserTraits } from '@confluence/targeting/entry-points/useSiteUserTraits';

import { useIsEligibleRovoAnnouncement } from './useIsEligibleRovoAnnouncement';

const TRAIT_HAS_USED_CREATE_WITH_ROVO = 'confluence_has_used_create_with_rovo';

const ANALYTICS_SOURCE = 'createWithRovoAnnouncementSplashModal';

export const useRovoAnnouncementSplashModal = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setOnboardingState } = useSetOnboardingState();

	const {
		isEligible,
		loading: isLoadingEligibility,
		error: eligibilityError,
	} = useIsEligibleRovoAnnouncement();

	const {
		data: dataTraits,
		loading: isLoadingTraits,
		error: errorTraits,
	} = useSiteUserTraits({ skipBootstrap: !isEligible });
	const hasUsedCreateWithRovo =
		dataTraits?.attributes?.find(
			({ name }: { name: string }) => name === TRAIT_HAS_USED_CREATE_WITH_ROVO,
		)?.value === 'true';

	const {
		data,
		loading: isLoadingHasSeenModal,
		error: errorHasSeenModal,
	} = useGetOnboardingState(Object.values(CREATE_WITH_ROVO_ANNOUNCEMENT_SPLASH), !isEligible);
	const { hasSeenSplashModal } = deserializeState(data);

	const loading =
		isLoadingEligibility || isLoadingTraits || (isEligible ? isLoadingHasSeenModal : false);
	const error = eligibilityError || errorTraits || (isEligible ? errorHasSeenModal : undefined);

	const isModalVisible = useMemo(
		() => isEligible && !hasUsedCreateWithRovo && !hasSeenSplashModal && !loading && !error,
		[isEligible, hasUsedCreateWithRovo, hasSeenSplashModal, loading, error],
	);

	const onModalShown = useCallback(
		(dismissSource: string) => {
			if (isModalVisible) {
				setOnboardingState({
					key: CREATE_WITH_ROVO_ANNOUNCEMENT_SPLASH.HAS_SEEN_SPLASH_MODAL,
					value: 'true',
				});
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'dismiss',
						attributes: {
							dismissSource,
						},
						source: ANALYTICS_SOURCE,
					},
				}).fire();
			}
		},
		[isModalVisible, createAnalyticsEvent, setOnboardingState],
	);

	return {
		isModalVisible,
		onModalShown,
		loading,
		error,
	};
};
